import { CurrencyEnum } from '../../lib/enums/currency.enum';
import lang from '../../lib/language';
import { roundNumberToTwoDecimals } from '../../lib/util/DataHelpers';
import { roundToCurrencyFormat } from '../../lib/util/lastPriceChangeHelpers';

import {
  csvData,
  depositAndWithdrawalOrders,
  FEES_BY_DESTINATION_TOTALS_ORDER,
  reportDataTypes,
  startEndBalanceOrder,
  TOTAL_KEYWORDS,
  totalAllSymbols,
  totalTradingFees,
  tradesAndPositionsOrder,
  tradingFeesSummaryOrder,
} from './reports.constants';
import { reportEntry, reportItem } from './Reports.types';

const roundNumberToFourDecimals = (value: number) => (Math.round((value + Number.EPSILON) * 10000) / 10000);

function isSymbolRowsExist(item, symbol: string):boolean {
  return item.key && item.key === symbol
      && item.value && item.value.rows
      && Array.isArray(item.value.rows)
      && item.value.rows.length > 0;
}

function getTradingFeesTotal(item): number {
  return purifyData(item.ecnFee + item.taf + item.sec + item.clr + item.ptfpf + item.nscc);
}

function getBalanceArray(item): Array<string | number> {
  return [
    item.currency,
    item.cash,
    item.unrealizedPnL,
    item.total,
  ];
}

function getTotalsArray(item): Array<string | number> {
  return [
    item.currency,
    item.orders,
    item.fills,
    item.qty,
    roundNumberToFourDecimals(item.gross),
    roundNumberToFourDecimals(item.comm),
    getTradingFeesTotal(item),
    roundNumberToFourDecimals(item.net),
  ];
}

function getTradesArray(item): Array<string | number> {
  return [
    new Date(item.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
    item.orderId,
    item.fillId,
    item.rout,
    item.liq,
    item.typ,
    item.qty,
    item.price,
    item.position,
    roundNumberToFourDecimals(item.gross),
    roundNumberToFourDecimals(item.comm),
    getTradingFeesTotal(item),
    roundNumberToFourDecimals(item.net),
  ];
}

function getOrdersArray(item): Array<string | number> {
  return [
    item.key === 'Total Option' ? lang.totalHeader() : item.key,
    '',
    '',
    item.value.orders,
    '',
    item.value.fills,
    item.value.qty,
    item.value.price,
    item.value.position,
    roundNumberToFourDecimals(item.value.gross),
    roundNumberToFourDecimals(item.value.comm),
    getTradingFeesTotal(item.value),
    roundNumberToFourDecimals(item.value.net),
  ];
}

function getAllTradingFees(item): Array<string | number> {
  return [
    item.currency,
    getTradingFeesTotal(item),
    roundNumberToFourDecimals(item.ecnFee),
    roundNumberToFourDecimals(item.taf),
    roundNumberToFourDecimals(item.sec),
    roundNumberToFourDecimals(item.clr),
    roundNumberToFourDecimals(item.ptfpf),
    roundNumberToFourDecimals(item.nscc),
  ];
}

function getTradingFees(item): Array<string | number> {
  return [
    item.value.currency,
    roundNumberToFourDecimals(item.ecnFee + item.taf + item.nscc),
    roundNumberToFourDecimals(item.value.ecnFee),
    roundNumberToFourDecimals(item.value.taf),
    roundNumberToFourDecimals(item.value.nscc),
  ];
}

function getSummariesByDateTradesArray(item, total): Array<string | number> {
  return [
    total ? item.currency : item.assetType,
    item.orders,
    item.fills,
    item.qty,
    roundNumberToFourDecimals(item.gross),
    roundNumberToFourDecimals(item.comm),
    roundNumberToFourDecimals(item.ecnFee + item.taf + item.sec + item.clr + item.ptfpf + item.nscc),
    roundNumberToFourDecimals(item.net),
    item.unrealized,
  ];
}

function getNonTradingFeesArray(item): Array<string | number> {
  if (item.adjustmentFees) {
    return [
      item.currency,
      item.adjustmentFees.dailyInterest + item.adjustmentFees.miscellaneous + item.adjustmentFees.software,
      item.adjustmentFees.dailyInterest,
      item.adjustmentFees.miscellaneous,
      item.adjustmentFees.software,
    ];
  }
  return [];
}

function getSummariesByDateTotalsArray(item): Array<string | number> {
  return [
    item.currency,
    purifyData(item.cash),
    purifyData(item.unrealized),
  ];
}

function getDepositAndWithdrawalsArray(item): Array<string | number> {
  return [
    item.currency,
    purifyData(item.transfers),
  ];
}

export const reportGenerationDate = () => {
  const currentDate = new Date();
  const day = currentDate.getDate().toString().padStart(2, '0');
  const month = new Intl.DateTimeFormat('en-US', { month: 'short' }).format(currentDate);
  const year = currentDate.getFullYear();
  return `${day} ${month} ${year}`;
};

export const purifyData = (item) => {
  if (item === null) {
    return 0;
  }

  if (Number.isNaN(item)) {
    return 0;
  }

  if (typeof item === 'number') {
    return roundToCurrencyFormat(item);
  }
  return item;
};

export const getStartEndBalance = (rawData) => {
  const data = startEndBalanceOrder.map(key => purifyData(rawData[key]));
  if (typeof data === 'undefined') {
    return [];
  }
  return data;
};

export const isNotEmptyBalance = (rawData: any) => {
  if (rawData.startBalance.cash === 0
    && rawData.startBalance.total === 0
    && rawData.endBalance.cash === null
    && rawData.endBalance.total === null
  ) {
    return false;
  }

  return true;
};

export const getAdjustments = (data) => [
  data.currency,
  data.quantityBought,
  data.quantitySold,
  data.valueBought,
  data.valueSold,
  data.comm,
  data.ecnFee,
  data.sec,
  data.taf,
  data.nscc,
  data.eftt,
];

export const processData = (rawData, drop: string[]): Array<number | string> => {
  const result = { ...rawData };
  drop.forEach(property => {
    // eslint-disable-next-line no-prototype-builtins
    if (result.hasOwnProperty(property)) {
      delete result[property];
    }
  });

  return Object.values(result).map(item => purifyData(item));
};

export const getBalance = (data) =>
  [data.currency, data.cash.toFixed(2), data.unrealizedPnL.toFixed(2), data.total.toFixed(2)];

export const totalFees = (rawData) => (
  roundToCurrencyFormat(
    rawData.ecnFee
    + rawData.sec
    + rawData.taf
    + rawData.nscc
    + rawData.clr
    + rawData.orf
    + rawData.ptfpf
    + rawData.eftt,
  )
);

export const getTradingFeesSummary = (rawData: any) => {
  const data = tradingFeesSummaryOrder.map(key => rawData[key]);
  data.splice(1, 0, totalFees(rawData));
  return data.map(item => purifyData(item));
};

export const getCsvData = (rawData: any) => {
  const data = csvData.map(key => rawData[key]);
  return data.map(item => purifyData(item));
};

export const getNetValue = (rawData: any, sector: string) => {
  const data = rawData.find(item => item.key === sector);
  if (typeof data === 'undefined') {
    return 0;
  }

  return data.value.net === null ? 0 : purifyData(data.value.net);
};

export const getTotalForAllSymbols = (rawData: any) => {
  let data = rawData.find(item => TOTAL_KEYWORDS.includes(item.key));
  if (typeof data === 'undefined') {
    return [];
  }
  const fees = purifyData(totalFees(data.value));
  data = totalAllSymbols.map(key => purifyData(data.value[key]));
  data.splice(6, 0, fees);
  return data;
};

export const getTotalSymbolsTradingFees = (rawData: reportEntry[]) => {
  const data = rawData.find(item => TOTAL_KEYWORDS.includes(item.key));
  if (typeof data === 'undefined') {
    return 0;
  }
  return totalFees(data.value);
};

export const getTotalTradingFees = (rawData: any) => {
  let data = rawData.find(item => TOTAL_KEYWORDS.includes(item.key));
  if (typeof data === 'undefined') {
    return [];
  }
  const fees = purifyData(totalFees(data.value));
  data = totalTradingFees.map(key => data.value[key]);
  data.splice(1, 0, fees);
  return data;
};

export const getTradesAndPositions = (rawData: any) => {
  let data = tradesAndPositionsOrder.map(key => rawData[key]);
  data = data.map(item => purifyData(item));
  data.splice(6, 0, totalFees(rawData));
  return data;
};

export const getDepositAndWithdrawals = (rawData: reportItem[]): Array<string | number> => {
  let currency = '';

  // Temp fix for missing currency in BE response.
  // TODO: Refactor this when BE response is fixed HW-239
  try {
    if (rawData[0].currency) {
      currency = rawData[0].currency;
    }
  } catch {
    currency = '-';
  }

  const values = [currency];
  depositAndWithdrawalOrders.forEach((key: string) => {
    const data = rawData.find(item => item.symbol === key);
    if (typeof data === 'undefined') {
      values.push('0');
    } else {
      values.push(purifyData(data.net));
    }
  });
  return values;
};

export const getNonTradingFees = (rawData: any) => {
  const headers: string[] = [];
  const values: string[] = [];
  let total = 0;
  let currency: string;

  // Temp fix for missing currency in BE response.
  // TODO: Refactor this when BE response is fixed HW-239
  try {
    currency = rawData[0].currency;
  } catch {
    currency = '-';
  }
  headers.push(lang.currencyHeader());
  values.push(currency);
  rawData.forEach((item: reportItem) => {
    headers.push(purifyData(item.symbol));
    values.push(purifyData(item.net));
    total += purifyData(item.net);
  });

  headers.splice(1, 0, lang.totalHeader());
  values.splice(1, 0, total.toString());

  return { headers, values };
};

export const getTrades = (items, fees?: string) => {
  if (fees) {
    return items.filter(item => item.assetTypeId !== 'Stock' && item.assetTypeId !== 'ETF');
  }
  return items.filter(item => item.assetTypeId === 'Stock' || item.assetTypeId === 'ETF');
};

export const getFeesByDestinationTotals = (rawData: reportItem) => {
  const data = FEES_BY_DESTINATION_TOTALS_ORDER.map(key => purifyData(rawData[key]));
  data.push(totalFees(rawData));
  data.splice(6, 0, '-');
  return data;
};

export const getFeesByDestination = (data: reportItem) => [
  [data.liquidity, data.currency, lang.ecnHeader().toUpperCase(), data.ecnFee, data.rateECN, data.valuePercECN],
  [data.liquidity, data.currency, lang.secHeader().toUpperCase(), data.sec, data.rateSEC, data.valuePercSEC],
  [data.liquidity, data.currency, lang.tafHeader().toUpperCase(), data.taf, data.rateTAF, data.valuePercTAF],
  [data.liquidity, data.currency, lang.nsccHeader().toUpperCase(), data.nscc, data.rateNSCC, data.valuePercNSCC],
  [data.liquidity, data.currency, lang.efttHeader().toUpperCase(), data.eftt, data.rateEftt, data.valuePercEftt],
];

export const getFeesByDestinationTotalTradingFees = (data: reportItem[]) => {
  let ecnFee = 0;
  let rateECN = 0;
  let valuePercECN = 0;
  let sec = 0;
  let rateSEC = 0;
  let valuePercSEC = 0;
  let taf = 0;
  let rateTAF = 0;
  let valuePercTAF = 0;
  let nscc = 0;
  let rateNSCC = 0;
  let valuePercNSCC = 0;
  let eftt = 0;
  let rateEftt = 0;
  let valuePercEftt = 0;

  data.forEach((item: reportItem) => {
    ecnFee += item.ecnFee!;
    rateECN += item.rateECN!;
    valuePercECN += item.valuePercECN!;
    sec += item.sec!;
    rateSEC += item.rateSEC!;
    valuePercSEC += item.valuePercSEC!;
    taf += item.taf!;
    rateTAF += item.rateTAF!;
    valuePercTAF += item.valuePercTAF!;
    nscc += item.nscc!;
    rateNSCC += item.rateNSCC!;
    valuePercNSCC += item.valuePercNSCC!;
    eftt += item.eftt!;
    rateEftt += item.rateEftt!;
    valuePercEftt += item.valuePercEftt!;
  });

  return [
    ['', data[0].currency, lang.ecnHeader().toUpperCase(), ecnFee, rateECN, valuePercECN],
    ['', data[0].currency, lang.secHeader().toUpperCase(), sec, rateSEC, valuePercSEC],
    ['', data[0].currency, lang.tafHeader().toUpperCase(), taf, rateTAF, valuePercTAF],
    ['', data[0].currency, lang.nsccHeader().toUpperCase(), nscc, rateNSCC, valuePercNSCC],
    ['', data[0].currency, lang.efttHeader().toUpperCase(), eftt, rateEftt, valuePercEftt],
  ];
};

export const getFeesByDestinationTotalReportPages = (data) => {
  let pageCounter = 0;

  data.forEach((item) => {
    item.items.forEach((subItem) => {
      if (subItem.totals !== null && subItem.totals.length > 0) {
        pageCounter += 1;
      }
      if (subItem.rows !== null && subItem.rows.length > 0) {
        const rows = subItem.rows.length;
        pageCounter += 2;
        if (rows > 3) {
          const pages = Math.floor(rows / 3);
          pageCounter += pages;
        }
      }
    });
  });

  return pageCounter;
};

export const calculateAdjustmentsFees = (data) => {
  const fees = data.map(fee => [
    fee.key.split(': ')[1],
    fee.value.currency,
    fee.value.debit,
    fee.value.credit,
    fee.value.net,
  ]);
  return fees;
};

export const calculateAdjustments = (data) => {
  const fees = data.flatMap(item => item.totals.filter(total => total.key.includes('Fee:')));
  const transfers = data.flatMap(item => item.totals.filter(total => total.key.includes('Transfer:')));
  const allFees = [...fees, ...transfers];

  const totalDebit = [0, 0];
  const totalCredit = [0, 0];
  const totalNet = [0, 0];

  allFees.forEach((fee: any) => {
    if (fee.value.currency === CurrencyEnum.USD) {
      totalDebit[0] += fee.value.debit;
      totalCredit[0] += fee.value.credit;
      totalNet[0] += fee.value.net;
    }
    if (fee.value.currency === CurrencyEnum.EUR) {
      totalDebit[1] += fee.value.debit;
      totalCredit[1] += fee.value.credit;
      totalNet[1] += fee.value.net;
    }
  });

  return { totalDebit, totalCredit, totalNet };
};

export const getAdjustmentsTotals = (totalDebit, totalCredit, totalNet) => {
  const totals: Array<Array<number | string>> = [];
  totals.push(['', CurrencyEnum.USD, roundNumberToTwoDecimals(totalDebit[0]), roundNumberToTwoDecimals(totalCredit[0]), roundNumberToTwoDecimals(totalNet[0])]);
  if (totalDebit[1] > 0 || totalCredit[1] > 0 || totalNet[1] > 0) {
    totals.push(['', CurrencyEnum.EUR, roundNumberToTwoDecimals(totalDebit[1]), roundNumberToTwoDecimals(totalCredit[1]), roundNumberToTwoDecimals(totalNet[1])]);
  }
  return totals;
};

export const getAdjustmentsDates = (data): string[] => {
  const uniqueDatesSet = new Set<string>();
  data.forEach(currency => {
    currency.items[0].rows.forEach(item => {
      uniqueDatesSet.add(item.date);
    });
  });

  return Array.from(uniqueDatesSet);
};

export const getAdjustmentsByDate = (date: string, data: any) => {
  const adjustmentsByDate = data.flatMap(item => item.items[0].rows.filter(adjustment => date === adjustment.date));
  const adjustmentsByDateArray = adjustmentsByDate.map(item => [item.category, item.comment === null ? '-' : item.comment, item.currency, item.debit, item.credit]);
  return adjustmentsByDateArray;
};

export const getSummaryDates = (data): string[] => {
  const uniqueDatesSet = new Set<string>();
  data.forEach((currency) => {
    currency.rows.forEach((item) => {
      uniqueDatesSet.add(item.balanceDate);
    });
  });
  return Array.from(uniqueDatesSet);
};

export const getSummaryByDate = (type: string, data: any, date?: string, total?: boolean) => {
  let summariesByDate = data.flatMap((item) => item.rows.filter((summary) => date === summary.balanceDate));
  if (total) {
    summariesByDate = data.flatMap((item) => item.totals.filter((totalItem) => totalItem.key === 'total'));
    summariesByDate = summariesByDate.map((item) => item.value);
  }
  const summariesByDateArray: Array<string | number>[] = [];
  switch (type) {
    case reportDataTypes.trades:
      summariesByDate.forEach((item) => {
        summariesByDateArray.push(getSummariesByDateTradesArray(item, total));
      });
      break;
    case reportDataTypes.tradingFees:
      summariesByDate.forEach((item) => {
        summariesByDateArray.push(getAllTradingFees(item));
      });
      break;
    case reportDataTypes.nonTradingFees:
      summariesByDate.forEach((item) => {
        summariesByDateArray.push(getNonTradingFeesArray(item));
      });
      break;
    case reportDataTypes.total:
      summariesByDate.forEach((item) => {
        summariesByDateArray.push(getSummariesByDateTotalsArray(item));
      });
      break;
    case reportDataTypes.depositsAndWithdrawals:
      summariesByDate.forEach((item) => {
        summariesByDateArray.push(getDepositAndWithdrawalsArray(item));
      });
      break;
  }
  return summariesByDateArray;
};
export const mergeArrays = (arr1: Array<Array<string | number>>, arr2: Array<Array<string | number>>) => {
  const length1 = arr1.length;
  const length2 = arr2.length;
  const paddedArr1 = length1 < length2 ? arr1.concat(Array(length2 - length1).fill([])) : arr1;
  const paddedArr2 = length2 < length1 ? arr2.concat(Array(length1 - length2).fill([])) : arr2;
  const mergedArray = paddedArr1.map((subarray1, index) => {
    const subarray2 = paddedArr2[index];
    return subarray1.concat([''], subarray2);
  });
  return mergedArray;
};

export const extractDatesFromRows = (data): string[] => {
  const resultArray: string[] = [];

  data.forEach((currencyData) => {
    if (currencyData.dailyMovements && Array.isArray(currencyData.dailyMovements)) {
      currencyData.dailyMovements.forEach((movement) => {
        if (movement.value?.items && Array.isArray(movement.value.items)) {
          movement.value.items.forEach((item) => {
            if (item.key
              && item.value?.rows
              && Array.isArray(item.value.rows)
              && item.value.rows.length > 0
            ) {
              resultArray.push(item.key);
            }
          });
        }
      });
    }
  });

  return resultArray;
};

export const extractDetailedData = (data:any, symbol: string, type: string) => {
  const resultArray: any = [];

  data.forEach((currencyData) => {
    if (type === reportDataTypes.startBalance) {
      resultArray.push(currencyData.startBalance);
    } else if (type === reportDataTypes.endBalance) {
      resultArray.push(currencyData.endBalance);
    } else if (type === reportDataTypes.totals || type === reportDataTypes.allTradingFees) {
      currencyData.totals.forEach((total) => {
        if (total.key && total.key === reportDataTypes.pascalCaseTotal) {
          resultArray.push(total.value);
        }
      });
    } else if (currencyData.dailyMovements && Array.isArray(currencyData.dailyMovements)) {
      currencyData.dailyMovements.forEach((movement) => {
        if (movement.value?.items && Array.isArray(movement.value.items)) {
          movement.value.items.forEach((item) => {
            if (type === reportDataTypes.trades && isSymbolRowsExist(item, symbol)) {
              resultArray.push(item.value.rows);
            }
            if ((type === reportDataTypes.orders || type === reportDataTypes.tradingFees)
              && isSymbolRowsExist(item, symbol)) {
              resultArray.push(item.value.totals);
            }
          });
        }
      });
    }
  });

  const filteredArray: Array<Array<string | number>> = [];

  switch (type) {
    case reportDataTypes.startBalance:
    case reportDataTypes.endBalance:
      resultArray.forEach((item) => {
        filteredArray.push(getBalanceArray(item));
      });
      break;
    case reportDataTypes.totals:
      resultArray.forEach((item) => {
        filteredArray.push(getTotalsArray(item));
      });
      break;
    case reportDataTypes.trades:
      resultArray.forEach((item) => {
        item.forEach((row) => {
          filteredArray.push(getTradesArray(row));
        });
      });
      break;

    case reportDataTypes.orders:
      resultArray.forEach((item) => {
        item.forEach((row) => {
          filteredArray.push(getOrdersArray(row));
        });
      });
      break;

    case reportDataTypes.allTradingFees:
      resultArray.forEach((item) => {
        filteredArray.push(getAllTradingFees(item));
      });
      break;

    case reportDataTypes.tradingFees:
      resultArray.forEach((item) => {
        item.forEach((row) => {
          if (row.key === 'Total Stock') {
            filteredArray.push(getTradingFees(row));
          }
        });
      });
      break;
  }

  return filteredArray.map((item) => purifyData(item));
};
